import React, { useEffect, useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import AppStore, { TThingUserData, TFile, TUploadState } from "@awailio/backend/appStore/AppStore"
import Button, { EButtonType } from "@awailio/main/buttons/Button"
import { useHistory } from "react-router"
import { v4 as uuidv4 } from "uuid"

import Scanner from "@awailio/main/scanner/Scanner"

import "./thingEditor.css"
export enum EThingEditorTask {
  addThing = "new",
  addThingAssignPubLink = "newThingNewPubLink",
  edit = "edit",
}
interface IThingEditor {
  task: EThingEditorTask
  thingID?: string
}

type TFileStorage = {
  [key: string]: string
}
export type TPDFStorage = {
  [key: string]: { url: string; name: string }
}

const ThingEditor: React.FunctionComponent<IThingEditor> = (props) => {
  const appStore = AppStore.useStore()

  const [scannedPubID, setScannedPubID] = useState<string>()
  const [startScanner, setStartScanner] = useState<boolean>(false)

  const [images, setImages] = useState<TFileStorage>({})
  const [pdfs, setPdfs] = useState<TPDFStorage>({})

  const maxFiles = 15
  const maxMB = 100

  const [freeFileSlots, setFreeFileSlots] = useState(maxFiles)

  const [data, setData] = useState<TThingUserData>({
    name: "",
    description: "",
    files: [],
  })
  const [savedData, setSavedData] = useState<TThingUserData>({
    name: "",
    description: "",
    files: [],
  })

  const history = useHistory()

  const [once, setOnce] = useState(true)

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    return data.files.forEach((file) => URL.revokeObjectURL(file.preview))
  }, [])

  const hasContent = () => {
    let hasContent = false
    if (data.name || data.description) hasContent = true
    return hasContent
  }

  //###################################################
  //
  //      getFiles
  //
  //###################################################

  // images
  useEffect(() => {
    if (appStore.userStorage && props.thingID) {
      const imagesList = appStore.userStorage.child(
        `userData/${appStore.userUid}/files/${props.thingID}/images`
      )

      imagesList.listAll().then((imagesList) => {
        imagesList.items.map((item) =>
          item.getDownloadURL().then((url) => {
            setImages((_images) => ({ ..._images, [item.fullPath]: url }))
          })
        )
      })
    }
  }, [appStore.userStorage])

  // pdfs
  useEffect(() => {
    if (appStore.userStorage) {
      const filesList = appStore.userStorage.child(
        `userData/${appStore.userUid}/files/${props.thingID}/files`
      )

      filesList.listAll().then((pdfList) => {
        pdfList.items.map((item) =>
          item.getDownloadURL().then((url) => {
            setPdfs((_pdfs) => ({ ..._pdfs, [item.fullPath]: { url: url, name: item.name } }))
          })
        )
      })
    }
  }, [appStore.userStorage])

  /**
   * add a new Thing
   * @param data
   */
  const updateThing = (data: TThingUserData, thingID: string) => {
    console.log("EDIT THING", data)
    setSavedData(data)
    appStore.updateThing(data, thingID)
  }

  /**
   * add a new Thing
   * @param _data
   */
  const addThing = (_data: TThingUserData) => {
    appStore.addThing(_data).then(() => {
      setData({ name: "", description: "", files: [] })
    })
    // setUploadWip(uploadState.inProgress)
  }

  /**
   * add a thing and assign it to an existing publink
   * @param _data
   */

  const deleteThing = () => {
    if (props.thingID) {
      appStore.deleteThing(props.thingID).then(() => {
        history.push("/")
      })
    }
  }

  //###################################################
  //
  //      Upload Progress
  //
  //###################################################

  useEffect(() => {
    if (once) {
      if (props.task === EThingEditorTask.edit && props.thingID !== undefined) {
        if (
          appStore.thingsSnapshot[props.thingID]?.name !== undefined &&
          appStore.thingsSnapshot[props.thingID]?.description !== undefined
        ) {
          console.log("once", once, props.thingID)

          let _data: TThingUserData = {
            name: appStore.thingsSnapshot[props.thingID]?.name || "",
            description: appStore.thingsSnapshot[props.thingID]?.description || "",
            files: [],
          }

          setData(_data)
          setOnce(false)
          console.log(_data)
        }
      }
    }
  }, [props])

  const active = () => {
    if (data.name || data.description) {
      // alert(JSON.stringify(data))
      return true
    } else return false
  }

  const canSave = () => {
    if (data.name || data.description) {
      if (data.name !== savedData.name || data.description !== savedData.description) {
        return true
      } else return false
    } else return false
  }

  const actionBar = (_task: EThingEditorTask) => {
    switch (_task) {
      case EThingEditorTask.edit:
        return (
          <div className="thingEditor__actionBarWrapper right">
            <Button
              onClick={() => deleteThing()}
              type={active() ? EButtonType.danger : EButtonType.transparent}
            >
              clear card
            </Button>
            <div className="thingEditor__actionBarSpacer"></div>
            <Button
              minWidth={8}
              type={canSave() ? EButtonType.primary : EButtonType.inactive}
              onClick={() => {
                if (props.thingID) {
                  updateThing(data, props.thingID)
                }
              }}
            >
              save
            </Button>
          </div>
        )

      case EThingEditorTask.addThing:
        return (
          <div className="thingEditor__actionBarWrapper right">
            <div className="thingEditor__cardinfo btn_primary info"></div>
            <Button
              minWidth={8}
              type={hasContent() ? EButtonType.primary : EButtonType.inactive}
              onClick={() => {
                addThing(data)
              }}
            >
              save
            </Button>
          </div>
        )

      case EThingEditorTask.addThingAssignPubLink:
        return (
          <>
            <button
              className={`btn primary ${data.name ? null : "inactive"}`}
              onClick={() => {
                // addThingAssignPubLink(data, props.publicLinkID)
              }}
            >
              save
            </button>
          </>
        )

      default:
    }
  }

  //###################################################
  //
  //      File dropZone
  //
  //###################################################

  const onDrop = useCallback(
    (acceptedFiles) => {
      let uploadFileCount = acceptedFiles.length
      console.log("uploadFileCount:", uploadFileCount, freeFileSlots)
      const freeSlots = freeFileSlots - (Object.keys(pdfs).length + Object.keys(images).length)
      if (acceptedFiles.length > freeSlots) {
        uploadFileCount = freeSlots

        appStore.addError(
          "tooManyFiles",
          <div>
            Sorry only {maxFiles} files per Card are possible.{" "}
            {acceptedFiles.length -
              (maxFiles +
                freeFileSlots +
                Object.keys(pdfs).length +
                Object.keys(images).length)}{" "}
            files will be skipped. Do you need more ? <b>Let us know.</b>
          </div>
        )
      }

      let newFiles: Array<TFile> = []

      for (let i = 0; i < uploadFileCount; i++) {
        const file = Object.assign(acceptedFiles[i], {
          preview: URL.createObjectURL(acceptedFiles[i]),
        })

        // add uuid to files for ios camera upload
        const getExtension = /(?:\.([^.]+))?$/
        const extension = getExtension.exec(file.name)
        console.log("extension1: ", extension)

        if (extension) {
          console.log("extension2:  ", extension[0])
          const name = file.name.replace(extension[0], "")
          const newName = `${name}_____${uuidv4()}${extension[0]}`
          console.log("resr---->", name, " -->  ", newName)

          Object.defineProperty(file, "name", {
            writable: true,
            value: newName,
          })
        }

        newFiles.push(file)
      }

      console.log("files >>>>>> ", data.files)

      if (data.files) {
        // add the files to the current files.
        const allFiles = [...newFiles, ...data.files]
        setData((_data) => ({ ..._data, files: allFiles }))
      } else {
        // first batch of files.
        setData((_data) => ({ ..._data, files: newFiles }))
      }

      // Do something with the files
      console.log("acceptedFiles: ", acceptedFiles)
    },
    [data.files, freeFileSlots]
  )

  useEffect(() => {
    setFreeFileSlots(maxFiles - data.files.length)
  }, [data.files])

  const { fileRejections, getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: "image/jpeg, image/png,  application/pdf",
    // maxFiles: freeFileSlots,
    maxFiles: maxFiles, //- files.length,
    maxSize: maxMB * 1024 * 1024,
  })

  const thumbs = () => {
    return data.files.map((file, id) => {
      if (file.type.includes("image")) {
        return (
          <div key={id} className="thingEditor__thumbnail">
            <img src={file.preview} className="thingEditor__thumbnail_img" />
            <Button
              type={EButtonType.plainDanger}
              onClick={() => {
                const newFileList = [...data.files]
                newFileList.splice(id, 1)

                // setFiles(newFileList)
                setData((_data) => ({ ..._data, files: newFileList }))
              }}
              className="thingEditor__thumbnail_remove"
            >
              <i className="material-icon shadow">cancel</i>
            </Button>
          </div>
        )
      }
    })
  }

  const oldImages = () => {
    let oldImages: (JSX.Element | undefined)[]
    const imagesPathes = Object.keys(images)
    if (imagesPathes.length > 0) {
      return imagesPathes.map((path, id) => {
        return (
          <div key={"old" + id} className="thingEditor__thumbnail">
            <img src={images[path]} className="thingEditor__thumbnail_img" />
            <Button
              type={EButtonType.plainDanger}
              onClick={() => {
                const imageRef = appStore.userStorage?.child(path)
                imageRef
                  ?.delete()
                  .then(() => {
                    const newImages = { ...images }
                    delete newImages[path]
                    setImages(newImages)
                  })
                  .catch((err) => {
                    appStore.addError(path, JSON.stringify(err))
                  })
              }}
              className="thingEditor__thumbnail_remove"
            >
              <i className="material-icon shadow">cancel</i>
            </Button>
          </div>
        )
      })
    }
  }

  //###################################################
  //
  //      pdfList
  //
  //###################################################

  const newPDFs = () => {
    return data.files?.map((file, id) => {
      if (!file.type.includes("image")) {
        return (
          <div key={id} className="thingEditor__pdfListItem">
            <div className="thingEditor__pdfIcon">
              {pdfIcon}
              <span className="tooltipText">{cleanFileName(file.name)}</span>
            </div>

            <div className="pdfName">{cleanFileName(file.name)}</div>
            <Button type={EButtonType.plainDanger} className="thingEditor__pdfRemove">
              <i className="material-icon grey">cancel</i>
            </Button>
          </div>
        )
      }
    })
  }
  const oldPDFs = () => {
    const pdfPaths = Object.keys(pdfs)
    if (pdfPaths.length > 0) {
      return pdfPaths.map((path, id) => {
        return (
          <div key={id} className="thingEditor__pdfListItem">
            <div className="thingEditor__pdfIcon">
              {pdfIcon}
              <span className="tooltipText">{cleanFileName(pdfs[path].name)}</span>
            </div>

            <div className="pdfName">{cleanFileName(pdfs[path].name)}</div>
            <Button
              type={EButtonType.plainDanger}
              className="thingEditor__pdfRemove"
              onClick={() => {
                const pdfRef = appStore.userStorage?.child(path)
                pdfRef
                  ?.delete()
                  .then(() => {
                    const newPDFs = { ...pdfs }
                    delete newPDFs[path]
                    setPdfs(newPDFs)
                  })
                  .catch((err) => {
                    appStore.addError(path, JSON.stringify(err))
                  })
              }}
            >
              <i className="material-icon grey">cancel</i>
            </Button>
          </div>
        )
      })
    }
  }

  useEffect(() => {
    fileRejections.forEach(({ file, errors }) => {
      const reason = errors.map((err) => " " + err.message + "")
      appStore.addError(file.name, `${file.name}:  ${reason}`)
    })
  }, [fileRejections])

  const imageDataAvailable = () => {
    let available = false
    if (data.files.length > 0) {
      available = true
    }
    if (Object.keys(images).length > 0) {
      available = true
    }
    return available
  }

  useEffect(() => {
    if (appStore.uploadInProgress === TUploadState.done) {
      // alert("we are done here!")
      setTimeout(() => {
        history.push("/")
      }, 500)
    }
  }, [appStore.uploadInProgress])

  const getScan = (scan: string) => {
    const regex = /http:\/\/awail\.io\/getthing\/pubid\/(.*?)$/
    const results = regex.exec(scan)
    if (results?.[1]) {
      const result = results?.[1]
      console.log("scannes id: ", result)
      setScannedPubID(result)
    }
  }
  return (
    <div className="thingsExplorer__cards">
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <div className="thingCard__wrapper edit">
          <div className={`thingEditor__content`}>
            <textarea
              className="thingEditor__textarea name"
              rows={1}
              name="name"
              placeholder="Name"
              value={data.name}
              onChange={(e) => {
                setData((prevData) => ({ ...prevData, name: e.target.value }))
              }}
            />
            <textarea
              className="thingEditor__textarea description"
              placeholder="describe your thing..."
              rows={7}
              value={data.description}
              onChange={(e) => {
                setData((prevData) => ({ ...prevData, description: e.target.value }))
              }}
            />
            <div
              className={`thingEditor__thumbnailsWrapper ${
                imageDataAvailable() ? "populatetd" : ""
              }`}
            >
              {thumbs()}
              {oldImages()}
            </div>
            <div className="thingEditor__pdfFiles">
              {newPDFs()}
              {oldPDFs()}
            </div>
          </div>
          <div className="thingEditor__controlls">
            <Button
              type={EButtonType.plain}
              fill
              onClick={() => setStartScanner((state) => !state)}
            >
              <i className="material-icon">qr_code_scanner</i> Scann QrCode
            </Button>
            {startScanner ? <Scanner onScanCB={getScan}></Scanner> : null}
            <div className="thingEditor__actionBarWrapper right">
              <Button onClick={open} fill type={EButtonType.plain}>
                <i className="material-icon">add</i>add images & pdfs{" "}
                {Object.keys(pdfs).length + Object.keys(images).length + (maxFiles - freeFileSlots)}
                /{maxFiles}
              </Button>
            </div>
            {actionBar(props.task)}
          </div>
          <div className={`thingEditor__dropInfo  ${isDragActive ? "active" : ""}`}>
            <div>
              <h1>
                Drop up to <br />
                {maxFiles -
                  (Object.keys(pdfs).length +
                    Object.keys(images).length +
                    (maxFiles - freeFileSlots))}{" "}
                files here
              </h1>
              <h2>
                Type: jpg, png, pdf <br /> up to {maxMB}MB
              </h2>
              {/* <div style={{ fontWeight: "bold" }}>
                Accepted filetypes:
                <ul>
                  <li>PDF files</li>
                  <li>JPEG images</li>
                  <li>PNG images</li>
                </ul>
              </div> */}
            </div>
          </div>
          <div className={`thingEditor__processing ${appStore.uploadInProgress}`}>
            {/* {appStore.uploadInProgress} // {appStore.uploadProgress} */}
            <div
              className={`thingEditor__processingMessage ${
                appStore.uploadInProgress !== "done" ? "show" : ""
              }`}
            >
              Uploading files
            </div>
            <LoadingCircle progress={appStore.uploadProgress}></LoadingCircle>
            <div className="thingEditor__uploadProgress"></div>
            {/* <div>{Math.round(appStore.uploadProgress)}%</div> */}
            <div className={`thingEditor__uploadProgressState ${appStore.uploadInProgress}`}>
              <Button
                onClick={() => {
                  appStore.resetUploadState().then(() => {
                    history.push("/")
                  })
                }}
                type={EButtonType.darkOutline}
                minWidth={10}
                minHeight={4}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default ThingEditor

// LoadingCircle --> //

//import "./LoadingCircle.css"
interface ILoadingCircle {
  progress: number
}

export const LoadingCircle: React.FunctionComponent<ILoadingCircle> = (props) => {
  const pi = 3.14159265359
  const circumference = pi * 2 * 20
  const progress = props.progress / 100
  return (
    <div className="thingEditor__loader">
      <svg className="thingEditor__circularLoader" viewBox="25 25 50 50">
        <circle
          className="thingEditor__circularLoaderPath"
          style={{
            strokeDasharray: `${circumference * progress}, ${circumference * (1 - progress)}`,
          }}
          cx="50"
          cy="50"
          r="20"
          fill="none"
        />
      </svg>

      <div className={`thingEditor__loaderIcon ${props.progress > 99 ? "show" : ""}`}>check</div>
    </div>
  )
}

export const cleanFileName = (filename: string): string => {
  const regex = /(.*?)___.*?\.pdf/
  const pureFilename = regex.exec(filename)
  if (pureFilename?.[1]) {
    return pureFilename[1] + ".pdf"
  } else {
    return filename
  }
}

export const pdfIcon = (
  <svg width="34" height="34" viewBox="0 0 34 34">
    <g id="AwailioWeb" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="PDFIcon">
        <path
          d="M21.0848194,0 L30,9 L30,31.1666667 C30,32.7314735 28.7208136,34 27.1428571,34 L2.85714286,34 C1.27918643,34 0,32.7314735 0,31.1666667 L0,2.83333333 C0,1.26852654 1.27918643,0 2.85714286,0 L21.0848194,0 Z"
          id="Path"
          fill="var(--grey)"
        ></path>
        <path
          d="M21.0848194,0 L30,9 L23,9 C21.8954305,9 21,8.1045695 21,7 L21,0 L21.0848194,0 Z"
          id="Path"
          fillOpacity="0.4"
          fill="#000000"
        ></path>
        <path
          d="M8.9122619,22 L8.9122619,21.1455078 L8.15054315,20.9990234 L8.15054315,19.421875 L9.35171503,19.421875 C9.89533482,19.421875 10.3640848,19.3282878 10.757965,19.1411133 C11.1518452,18.9539388 11.4545796,18.6902669 11.6661682,18.3500977 C11.8777567,18.0099284 11.983551,17.6136068 11.983551,17.1611328 C11.983551,16.7086589 11.8777567,16.3123372 11.6661682,15.972168 C11.4545796,15.6319987 11.1518452,15.3666992 10.757965,15.1762695 C10.3640848,14.9858398 9.89533482,14.890625 9.35171503,14.890625 L9.35171503,14.890625 L5.96792597,14.890625 L5.96792597,15.75 L6.7247619,15.8964844 L6.7247619,20.9990234 L5.96792597,21.1455078 L5.96792597,22 L8.9122619,22 Z M9.35171503,18.3232422 L8.15054315,18.3232422 L8.15054315,15.9892578 L9.35171503,15.9892578 C9.74885044,15.9892578 10.0491434,16.1007487 10.2525939,16.3237305 C10.4560445,16.5467122 10.5577697,16.8291016 10.5577697,17.1708984 C10.5577697,17.5061849 10.4560445,17.7820638 10.2525939,17.9985352 C10.0491434,18.2150065 9.74885044,18.3232422 9.35171503,18.3232422 L9.35171503,18.3232422 Z M15.5761276,22 C16.2108933,22 16.7691615,21.8502604 17.2509323,21.5507812 C17.7327032,21.2513021 18.1078659,20.8387044 18.3764206,20.3129883 C18.6449753,19.7872721 18.7792526,19.1875 18.7792526,18.5136719 L18.7792526,18.5136719 L18.7792526,18.3818359 C18.7792526,17.7014974 18.6433477,17.0984701 18.3715378,16.5727539 C18.0997279,16.0470378 17.7213099,15.6352539 17.2362839,15.3374023 C16.7512579,15.0395508 16.191362,14.890625 15.5565964,14.890625 L15.5565964,14.890625 L12.4364792,14.890625 L12.4364792,15.75 L13.2030808,15.9160156 L13.2030808,20.984375 L12.4364792,21.1455078 L12.4364792,22 L15.5761276,22 Z M15.5028855,20.9111328 L14.628862,20.9111328 L14.628862,15.984375 L15.5028855,15.984375 C15.8967657,15.984375 16.2304245,16.0861003 16.503862,16.2895508 C16.7772995,16.4930013 16.9864467,16.7745768 17.1313034,17.1342773 C17.2761602,17.4939779 17.3485886,17.9082031 17.3485886,18.3769531 L17.3485886,18.3769531 L17.3485886,18.5136719 C17.3485886,18.9759115 17.2761602,19.3876953 17.1313034,19.7490234 C16.9864467,20.1103516 16.7772995,20.3943685 16.503862,20.6010742 C16.2304245,20.8077799 15.8967657,20.9111328 15.5028855,20.9111328 L15.5028855,20.9111328 Z M22.3452907,22 L22.3452907,21.1455078 L21.5835719,20.9990234 L21.5835719,19.0458984 L24.0738063,19.0458984 L24.0738063,17.9472656 L21.5835719,17.9472656 L21.5835719,15.9892578 L23.9029078,15.9892578 L23.9663844,16.8388672 L25.0601344,16.8388672 L25.0601344,14.890625 L19.4009547,14.890625 L19.4009547,15.75 L20.1577907,15.8964844 L20.1577907,20.9990234 L19.4009547,21.1455078 L19.4009547,22 L22.3452907,22 Z"
          id="PDF"
          fill="#FFFFFF"
          fillRule="nonzero"
        ></path>
      </g>
    </g>
  </svg>
)
